import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box,
    Checkbox,
    CircularProgress,
    Fab,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Description, Download, Search} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {useNavigate} from "react-router-dom";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {Client} from "../../../models/basic/client";
import Button from "@mui/material/Button";
import {sortListObj, sortListString} from "../../../utils/list-utils";
import {Form, Formik} from "formik";
import Select from "@mui/material/Select";
import {years} from "../../../models/default/year-month";
import TableActionComponent from "../../../components/table/table-action.component";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";
import {orderByFn} from "../../../utils/order-by";
import AddImportNreCostsComponent from "../import/add-import-nre-costs";
import {AssetControl} from "../../../models/basic/asset-control";
import {Project, statusList} from "../../../models/basic/project";
import {AssetControlFilter, AssetControlService} from "../../../services/asset-control-service";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {AbstractService} from "../../../services/abstract-service";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {ConvertDateToString} from "../../../components/date/date-timeFormat";
import {enUS, ptBR} from "date-fns/locale";
import {trackPromise} from "react-promise-tracker";
import MultilevelSelectYearComponent from "../../../components/multilevel-select-year/multilevel-select-year.component";
import {SelectItem} from "../../../models/basic/select-item";


const AssetControlComponent = () => {
    const navigate = useNavigate();
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);

    const service = new AssetControlService();
    const {filters, updateFilter, updateFilterKeepingParam, resetFilters} = useFilters<AssetControlFilter>(
        {client: "", ordering: "", project: "", date: "", active: "0"},
        ["client", "ordering", "project", "date", "active"]
    );
    const [assetControlList, setAssetControlList] = React.useState<PaginatedResult<AssetControl>>();
    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<AssetControl>(assetControlList);
    const expand = ["client", "lob", "project", "categories"];

    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);
    const [dataSourceProject, setDataSourceProject] = React.useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = React.useState<number[]>([]);

    const [loading, setLoading] = React.useState(false);

    const [openImportDialog, setOpenImportDialog] = React.useState(false);
    const [total, setTotal] = React.useState<AssetControl>(new AssetControl());

    const [loadingDownload, setLoadingDownload] = React.useState(false);

    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        loadClients();
        loadProjects();
        initializeSelectedProjects();
        search();
    }, []);

    useEffect(() => {
        if (!filters.project) {
            selectAllProjects();
        }
    }, [dataSourceProject]);

    const initializeSelectedProjects = () => {
        if (filters.project) {
            const projects = filters.project.split(",");
            setSelectedProject(projects.map((p: any) => parseInt(p)));
        }
    }

    const selectAllProjects = () => {
        setSelectedProject(dataSourceProject.map((p: any) => p.id));
        updateFilterKeepingParam("project", dataSourceProject.map((p: any) => p.id).join(","));
    }

    const loadClients = () => {
        serviceClient.readAll().then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceClient(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadMonthYearOptions = () => {
        service.readAll().then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceClient(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadProjects = (idClient?: any) => {
        let filter;
        if (idClient && idClient !== "0") {
            filter = {client: idClient};
        }
        service.listProjects(filter).then(response => {
            setDataSourceProject(sortListObj(response.data, "name"));
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        service.readPaginated(filters, expand, pagination).then(response => {
            setAssetControlList(response.data);
            calculateTotal(response.data.results);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
        restartIndex && resetPage();
    };

    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);

    const calculateTotal = (data: AssetControl[]) => {
        const total = new AssetControl();
        data.map((row: AssetControl) => {
            total.total_amortized += row.total_amortized;
            total.total_nre += row.total_nre;
            total.open_nre += row.open_nre;
        });
        setTotal(total);
    }

    const customCellRender = (column: string, index: number, row: AssetControl) => {
        if (column === "see_nfs") {
            return <Box width="50px">
                <Tooltip title={translate.t("see_nfs")} placement="top">
                    <IconButton
                        onClick={() => {
                            goToDetail(row);
                        }}
                    >
                        <Description/>
                    </IconButton>
                </Tooltip>
            </Box>
        }
        if (column === "project.name") {
            return (
                <Typography variant="body2" noWrap sx={{maxWidth: '200px'}}>
                    {activeIcon(row.project.active)}
                    {row.project.name}
                </Typography>
            );
        }
        return row[column as keyof AssetControl];
    }

    const renderProjects = (selected: any) => {
        if (allProjectsSelected()) {
            return translate.t('all');
        }
        if (loading) {
            return translate.t('loading');
        }
        const selects: any[] = [];
        selected.map((id: any) => {
            const description: any = dataSourceProject.find(project => id === project.id)?.name;
            selects.push(description);
        });

        const sortedSelects = sortListString(selects);
        return sortedSelects.join(", ");
    }

    const goToDetail = (row: AssetControl) => {
        resetFilters();
        navigate(`/reports/asset-control/${row.id}`);
    }

    const layoutDetail = (row: any) => {

        const map_values = row.categories.reduce((acc: Record<string, number>, category: any) => {
            acc[category.category.name.toUpperCase()] = category.total;
            return acc;
        }, {}) || {};
        const categories_names = row.categories?.map((category: any) => category.category.name.toUpperCase()) || [];

        return (
            <TablePaginationComponent
                key={row.id}
                state={[map_values]}
                displayedColumnsShow={categories_names}
                displayedColumns={categories_names}
                numberColumns={categories_names}
                secondaryLayout={true}
            />
        )
    }

    const allProjectsSelected = () => {
        return selectedProject.length === dataSourceProject.length;
    }

    const export_xlsx = (hash?: string, budget = true) => {
        search();
        setLoadingDownload(true);

        filters.budget = budget;

        service.export_xlsx(filters, expand).then(response => {
            if (response.data.hash) {
                get_export_status(response.data.hash, budget);
            }
        }).catch((error: any) => {
            ErrorComponent(error);
            setLoadingDownload(false);
        })
    }

    const get_export_status = (hash: string, budget = true) => {
        stopPollingFn();
        pollingRef.current = setInterval(() => {
            trackPromise(
                service.get_export_status(hash, budget).then(response => {
                    if (response.data.status === 2 || response.data.status === 3) {
                        stopPollingFn();
                        response.data.status === 2 && download(hash, budget);
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                    setLoadingDownload(false);
                })
            )
        }, 5000)
    }

    const download = (hash: string, budget = true) => {
        service.download({hash, budget}, '/download').then(response => {
            service.downloadFile(response);
        }).finally(() => {
            setLoadingDownload(false);
        });
    }

    const stopPollingFn = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const activeIcon = (active = true) => {
        return (
            <Tooltip title={active ? 'Projeto ativo' : 'Projeto inativo'} placement="top">
                <Box
                    sx={{
                        width: 15,
                        height: 15,
                        borderRadius: '50%',
                        backgroundColor: active ? '#43de92' : '#dbdbdb',
                        display: 'inline-block',
                        marginRight: '8px',
                        float: 'left'
                    }}
                />
            </Tooltip>
        );
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("report_asset_control")} - {translate.t("commodatum_agreements")}
                </Typography>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"}
                                                  width="80%">
                                                <Grid size={4}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client || "0"}
                                                            onChange={(event) => {
                                                                setSelectedProject([]);
                                                                updateFilterKeepingParam("client", event.target.value as string)
                                                                loadProjects(event.target.value as string);
                                                            }}
                                                        >
                                                            <MenuItem key="0" value="0" divider={true}>
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid >
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("status")}
                                                        </InputLabel>
                                                        <Select
                                                            name="status"
                                                            label={translate.t("status")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.active}
                                                            onChange={(event) => updateFilterKeepingParam("active", event.target.value)}
                                                        >
                                                            {statusList?.map((stts: SelectItem) => (
                                                                <MenuItem key={stts.value} value={stts.value}>
                                                                    {translate.t(stts.label)}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={4} maxWidth="400px">
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("project")}
                                                        </InputLabel>
                                                        <Select
                                                            name="project"
                                                            label={translate.t("project")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedProject}
                                                            onChange={(event: any) => {
                                                                if (event.target.value.includes(0)) {
                                                                    if (!allProjectsSelected()) {
                                                                        setSelectedProject(dataSourceProject.map((p: any) => p.id));
                                                                        updateFilterKeepingParam("project", dataSourceProject.map((p: any) => p.id).join(","));
                                                                        return;
                                                                    }
                                                                    setSelectedProject([]);
                                                                    updateFilterKeepingParam("project", "");
                                                                    return;
                                                                }
                                                                setSelectedProject(event.target.value)
                                                                updateFilterKeepingParam("project", event.target.value.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderProjects(selected)}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: '400px',
                                                                    },
                                                                },
                                                                disableAutoFocusItem: true,
                                                            }}
                                                            inputProps={{
                                                                sx: {
                                                                    maxWidth: '400px !important',
                                                                    width: '400px !important',
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem key={0} value={0}>
                                                                <Checkbox checked={allProjectsSelected()}/>
                                                                <ListItemText primary={
                                                                    <Box display="flex" alignItems="center"
                                                                         justifyContent="space-between">
                                                                        <Typography
                                                                            variant="body1">{translate.t('all')}</Typography>
                                                                        {allProjectsSelected() &&
                                                                            <Typography variant="body2"
                                                                                        color="gray">{translate.t('clear')}</Typography>}
                                                                    </Box>
                                                                }/>
                                                            </MenuItem>
                                                            {dataSourceProject?.map((p: any) => (
                                                                <MenuItem key={p.id} value={p.id}>
                                                                    <Checkbox checked={selectedProject.includes(p.id)}/>
                                                                    <ListItemText primary={p.name}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={1}>
                                                    <Fab
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                            <Grid alignItems="center" container justifyContent="flex-end" spacing={3}
                                                  width="20%">
                                                <Grid >
                                                    {loading && <CircularProgress size="30px"/>}
                                                </Grid>
                                                <Grid >
                                                    <Button
                                                        disabled={loadingDownload}
                                                        color="secondary"
                                                        variant="contained"
                                                        startIcon={<Download/>}

                                                        aria-controls={open ? 'demo-positioned-menu' : undefined}
                                                        aria-haspopup="true"
                                                        aria-expanded={open ? 'true' : undefined}
                                                        onClick={handleClick}
                                                        // onClick={() => {
                                                        //     export_xlsx();
                                                        // }}
                                                    >
                                                        {translate.t("export").toUpperCase() + ' ' + translate.t("report").toUpperCase()}
                                                        {loadingDownload &&
                                                            <CircularProgress size="20px" sx={{marginLeft: '10px'}}/>}
                                                    </Button>
                                                    <Menu
                                                        id="demo-positioned-menu"
                                                        aria-labelledby="demo-positioned-button"
                                                        anchorEl={anchorEl}
                                                        open={open}
                                                        onClose={handleClose}
                                                        anchorOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'left',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'left',
                                                        }}
                                                    >
                                                        <MenuItem onClick={() => export_xlsx()}>Exportar todo relatório</MenuItem>
                                                        <MenuItem onClick={() => export_xlsx(undefined, false)}>Exportar sem budget</MenuItem>
                                                    </Menu>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <CollapsibleTableComponent
                    state={dataSource}
                    displayedColumnsShow={["Client", "Platform", "LOB", "Amortized Qty", "Budget", "Total NRE", "Amortized Cost", "Open NRE", "action"]}
                    displayedColumns={["client.name", "project.name", "lob.name", "amortized_items", "project.budget", "total_nre", "total_amortized", "open_nre", "action"]}
                    numberColumns={["amortized_items", "total_nre", "total_amortized", "open_nre", "project.budget"]}
                    currencyColumns={["total_nre", "total_amortized", "open_nre", "project.budget"]}
                    totalColumns={["total_nre", "total_amortized", "open_nre"]}
                    integerColumns={["amortized_items"]}
                    highlightedColumns={["total_nre", "total_amortized", "open_nre"]}
                    highlightedColor={"#E5ECFF"}
                    customActions={"see_nfs"}
                    customColumns={["project.name"]}
                    total={total}
                    orderByColumns={["client.name"]}
                    orderBy={filters.ordering}
                    orderByFn={(column: string, orderDirection: any) => {
                        orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
                    }}
                    orderDirection={orderDirection}
                    actionsComponent={TableActionComponent}
                    childrenComponent={layoutDetail}
                    count={assetControlList?.count}
                    next={assetControlList?.next}
                    previous={assetControlList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                    customCellRender={customCellRender}
                    showActions={["detail"]}
                    goToDetail={goToDetail}
                />
            </Paper>

            <AddImportNreCostsComponent
                openDialog={openImportDialog}
                handleCloseDialog={() => {
                    search(true);
                    setOpenImportDialog(false);
                }}
                dataSourceClient={dataSourceClient}
            />
        </section>
    );
};

export default AssetControlComponent;
